import { useState } from "react";
import { z } from "zod";
import { fcfa, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis, Refus } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

// ===========================================================================
// JOURNAL DES ÉCRITURES
// ===========================================================================

const PieceSchema = z.object({
  date: z.string(),
  journal: z.string(),
  reference: z.string(),
  libelle: z.string(),
  origine: z.string(),
  motifAttente: z.string().nullable(),
  alternative: z.string().nullable(),
  totalDebit: z.number(),
  totalCredit: z.number(),
  e1Equilibree: z.boolean(),
  lignes: z.array(
    z.object({
      compte: z.string(),
      intitule: z.string(),
      tiers: z.string().nullable(),
      debit: z.number(),
      credit: z.number(),
      libelle: z.string(),
    }),
  ),
});

export function JournalEcritures({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["journal-ecritures", dossierId, annee],
    `/dossiers/${dossierId}/etats/journal?annee=${annee}`,
    z.array(PieceSchema),
  );

  const [ouverte, setOuverte] = useState<string | null>(null);

  if (q.isLoading) return <EtatChargement quoi="du journal" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="Le journal des écritures" />;

  const pieces = q.data.valeur;
  const enAttente = pieces.filter((p) => p.motifAttente !== null);

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Pièces", valeur: pieces.length },
          { titre: "Total débit", valeur: fcfa(pieces.reduce((s, p) => s + p.totalDebit, 0)) },
          { titre: "Total crédit", valeur: fcfa(pieces.reduce((s, p) => s + p.totalCredit, 0)) },
          {
            titre: "En compte d'attente",
            valeur: enAttente.length,
            note: "471 — à arbitrer",
            alerte: enAttente.length > 0,
          },
          {
            titre: "Déséquilibrées",
            valeur: pieces.filter((p) => !p.e1Equilibree).length,
            alerte: pieces.some((p) => !p.e1Equilibree),
          },
        ]}
      />

      {enAttente.length > 0 && (
        <section>
          <h2>Pièces en attente d'arbitrage</h2>
          <p className="resume">
            Un flux non adossé ne devient jamais un produit ni une charge : il va
            en compte d'attente 471 et part en demande d'explication écrite.
            <strong> On nomme l'alternative plutôt que de deviner.</strong>
          </p>
          <ul className="attentes">
            {enAttente.map((p) => (
              <li key={p.reference}>
                <header>
                  <code>{p.reference}</code> {p.libelle} — {fcfa(p.totalDebit)}
                </header>
                <p className="attente__motif">{p.motifAttente}</p>
                {p.alternative && <p className="attente__alternative">{p.alternative}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2>Pièces</h2>
        <Tableau
          colonnes={[
            { cle: "date", titre: "Date", rendu: (p) => p.date },
            { cle: "jrn", titre: "Journal", rendu: (p) => p.journal },
            { cle: "ref", titre: "Pièce", rendu: (p) => <code>{p.reference}</code> },
            { cle: "lib", titre: "Libellé", rendu: (p) => p.libelle },
            {
              cle: "org",
              titre: "Origine",
              rendu: (p) => <span className="jeton jeton--origine">{p.origine}</span>,
            },
            { cle: "d", titre: "Débit", montant: true, rendu: (p) => fcfa(p.totalDebit) },
            { cle: "c", titre: "Crédit", montant: true, rendu: (p) => fcfa(p.totalCredit) },
            {
              cle: "e1",
              titre: "E1",
              rendu: (p) => (
                <span className={p.e1Equilibree ? "oui" : "non"}>
                  {p.e1Equilibree ? "équilibrée" : "déséquilibrée"}
                </span>
              ),
            },
          ]}
          lignes={pieces}
          cle={(p, i) => `${p.reference}-${i}`}
          surLigne={(p) => setOuverte(ouverte === p.reference ? null : p.reference)}
        />
      </section>

      {ouverte && (
        <section>
          <h2>Détail de la pièce {ouverte}</h2>
          <Tableau
            colonnes={[
              { cle: "cpt", titre: "Compte", rendu: (l) => <code>{l.compte}</code> },
              { cle: "int", titre: "Intitulé", rendu: (l) => l.intitule },
              { cle: "tiers", titre: "Tiers", rendu: (l) => l.tiers ?? "—" },
              { cle: "lib", titre: "Libellé", rendu: (l) => l.libelle },
              { cle: "d", titre: "Débit", montant: true, rendu: (l) => (l.debit ? fcfa(l.debit) : "") },
              { cle: "c", titre: "Crédit", montant: true, rendu: (l) => (l.credit ? fcfa(l.credit) : "") },
            ]}
            lignes={pieces.find((p) => p.reference === ouverte)?.lignes ?? []}
            cle={(l, i) => `${l.compte}-${i}`}
          />
        </section>
      )}

      <ListeAvis avis={q.data.avis} titre="Contrôles et observations" />
    </div>
  );
}

// ===========================================================================
// BALANCE GÉNÉRALE — format 6 colonnes MDC
// ===========================================================================

const LigneBalanceSchema = z.object({
  numero: z.string(),
  intitule: z.string(),
  mouvementDebit: z.number(),
  mouvementCredit: z.number(),
  soldeDebiteur: z.number(),
  soldeCrediteur: z.number(),
});

const BalanceSchema = z.object({
  bilan: z.array(LigneBalanceSchema),
  gestion: z.array(LigneBalanceSchema),
  hao: z.array(LigneBalanceSchema),
  totalMouvementDebit: z.number(),
  totalMouvementCredit: z.number(),
  totalSoldeDebiteur: z.number(),
  totalSoldeCrediteur: z.number(),
  resultatDesComptesDeGestion: z.number(),
  t1MouvementsEquilibres: z.boolean(),
  t2BalanceEquilibree: z.boolean(),
  t3ResultatCoherent: z.boolean(),
  estPubliable: z.boolean(),
});

export function BalanceGenerale({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["balance", dossierId, annee],
    `/dossiers/${dossierId}/etats/balance?annee=${annee}`,
    BalanceSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="de la balance" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <Refus avis={q.data?.avis ?? []} quoi="La balance générale" />;

  const b = q.data.valeur;

  const colonnes = [
    { cle: "n", titre: "N°", rendu: (l: z.infer<typeof LigneBalanceSchema>) => <code>{l.numero}</code> },
    { cle: "int", titre: "Intitulé", rendu: (l: z.infer<typeof LigneBalanceSchema>) => l.intitule },
    { cle: "md", titre: "Mvt D", montant: true, rendu: (l: z.infer<typeof LigneBalanceSchema>) => fcfa(l.mouvementDebit) },
    { cle: "mc", titre: "Mvt C", montant: true, rendu: (l: z.infer<typeof LigneBalanceSchema>) => fcfa(l.mouvementCredit) },
    { cle: "sd", titre: "Solde D", montant: true, rendu: (l: z.infer<typeof LigneBalanceSchema>) => (l.soldeDebiteur ? fcfa(l.soldeDebiteur) : "") },
    { cle: "sc", titre: "Solde C", montant: true, rendu: (l: z.infer<typeof LigneBalanceSchema>) => (l.soldeCrediteur ? fcfa(l.soldeCrediteur) : "") },
  ];

  return (
    <div>
      <p className="resume">Format 6 colonnes MDC.</p>

      <section className="controles">
        <h2>Contrôles</h2>
        <ul>
          <li className={b.t1MouvementsEquilibres ? "oui" : "non"}>
            T1 — mouvements équilibrés : {b.t1MouvementsEquilibres ? "passé" : "échoué"}
          </li>
          <li className={b.t2BalanceEquilibree ? "oui" : "non"}>
            T2 — balance équilibrée : {b.t2BalanceEquilibree ? "passé" : "échoué"}
          </li>
          <li className={b.t3ResultatCoherent ? "oui" : "non"}>
            T3 — résultat des comptes de gestion : {b.t3ResultatCoherent ? "passé" : "échoué"}
          </li>
        </ul>
      </section>

      {[
        { titre: "Comptes de bilan (classes 1 à 5)", lignes: b.bilan },
        { titre: "Comptes de gestion (classes 6 et 7)", lignes: b.gestion },
        { titre: "Hors activités ordinaires (classe 8)", lignes: b.hao },
      ].map((s) =>
        s.lignes.length === 0 ? null : (
          <section key={s.titre}>
            <h2>{s.titre}</h2>
            <Tableau colonnes={colonnes} lignes={s.lignes} cle={(l) => l.numero} />
          </section>
        ),
      )}

      <Tuiles
        tuiles={[
          { titre: "Total mouvements débit", valeur: fcfa(b.totalMouvementDebit) },
          { titre: "Total mouvements crédit", valeur: fcfa(b.totalMouvementCredit) },
          { titre: "Total soldes débiteurs", valeur: fcfa(b.totalSoldeDebiteur) },
          { titre: "Total soldes créditeurs", valeur: fcfa(b.totalSoldeCrediteur) },
          { titre: "Résultat des comptes de gestion", valeur: fcfa(b.resultatDesComptesDeGestion) },
        ]}
      />

      <ListeAvis avis={q.data.avis} titre="Contrôles et observations" />
    </div>
  );
}
